import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, Chip, DialogContent, DialogTitle, IconButton, Paper, Stack,
  Table, TableBody, TableCell, TableContainer, TableHead, TablePagination,
  TableRow, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded';

import AppDialog from './AppDialog';
import EmptyState from './EmptyState';
import { EMPTY_VALUE, formatDate, formatEf } from '../utils/number';

const hasValue = (value) => value !== null && value !== undefined && value !== '';
const HISTORY_PAGE_SIZE = 10;
const HISTORY_COLUMN_WIDTHS = [165, 120, 125, 220, 95, 125, 165, 105, 195, 175];

const actionLabel = (entry, t) => {
  if (entry.change_type === 'EF_VALIDATION_FAILED') {
    return t('emissionFactor.historyTable.validationFailed');
  }
  if (entry.change_type === 'EF_UPSERT') {
    return Object.keys(entry.before || {}).length > 0
      ? t('emissionFactor.historyTable.newVersion')
      : t('emissionFactor.historyTable.created');
  }
  return String(entry.change_type || EMPTY_VALUE).replaceAll('_', ' ');
};

const actionColor = (entry) => {
  if (entry.change_type === 'EF_VALIDATION_FAILED') return 'error';
  if (entry.change_type === 'EF_UPSERT' && Object.keys(entry.before || {}).length > 0) {
    return 'success';
  }
  return 'primary';
};

const snapshotOf = (entry) => (
  Object.keys(entry.after || {}).length > 0 ? entry.after : (entry.before || {})
);

export const evidenceOf = (snapshot) => {
  const locator = snapshot.source_locator;
  if (!locator) return EMPTY_VALUE;
  if (typeof locator === 'string') return locator;
  return [
    locator.sheet_name || locator.sheet,
    locator.value_cell || locator.cell || locator.range,
  ]
    .filter(Boolean)
    .join(' · ') || JSON.stringify(locator);
};

function ValueSnapshot({ entry }) {
  const beforeValue = entry.before?.factor_value ?? entry.before?.ef_value;
  const afterValue = entry.after?.factor_value ?? entry.after?.ef_value;
  const formatValue = (value) => formatEf(value);
  const sameDisplayedValue = hasValue(beforeValue)
    && hasValue(afterValue)
    && formatEf(beforeValue) === formatEf(afterValue);

  if (!hasValue(beforeValue) && !hasValue(afterValue)) return EMPTY_VALUE;
  if (!hasValue(beforeValue) || beforeValue === afterValue || sameDisplayedValue) {
    return (
      <Typography variant="caption" fontWeight={650} noWrap sx={{ fontFamily: 'monospace' }}>
        {formatValue(hasValue(afterValue) ? afterValue : beforeValue)}
      </Typography>
    );
  }

  return (
    <Stack direction="row" spacing={0.5} alignItems="center" useFlexGap flexWrap="wrap">
      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
        {formatValue(beforeValue)}
      </Typography>
      <ArrowRightAltIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
      <Typography variant="caption" fontWeight={750} sx={{ fontFamily: 'monospace' }}>
        {formatValue(afterValue)}
      </Typography>
    </Stack>
  );
}

export default function AuditHistoryDialog({ open, onClose, onExited, context, entries = [] }) {
  const { t } = useTranslation();
  const [page, setPage] = useState(0);
  const pageCount = Math.max(1, Math.ceil(entries.length / HISTORY_PAGE_SIZE));
  const pageEntries = entries.slice(
    page * HISTORY_PAGE_SIZE,
    page * HISTORY_PAGE_SIZE + HISTORY_PAGE_SIZE,
  );
  const currentEntryIndex = entries.findIndex((entry) => (
    entry.change_type === 'EF_UPSERT' && Object.keys(entry.after || {}).length > 0
  ));

  useEffect(() => {
    setPage(0);
  }, [context?.externalId, open]);

  useEffect(() => {
    if (page >= pageCount) setPage(pageCount - 1);
  }, [page, pageCount]);

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      TransitionProps={{ onExited }}
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: {
          m: { xs: 1, sm: 2 },
          maxWidth: 1760,
          width: { xs: 'calc(100% - 16px)', sm: 'calc(100% - 32px)' },
        },
      }}
    >
      <DialogTitle sx={{ px: { xs: 2, sm: 3 }, py: 2.25, pr: 7 }}>
        <Typography component="h2" variant="h6" fontWeight={800}>
          {t('common.auditHistory')}
        </Typography>
        <IconButton
          onClick={onClose}
          aria-label={t('common.close')}
          sx={{ position: 'absolute', right: 16, top: 14 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        <Box sx={{ p: { xs: 2, sm: 3 } }}>
          {entries.length === 0 ? (
            <EmptyState title={t('common.noHistory')} description={t('common.noHistoryHint')} />
          ) : (
            <Paper variant="outlined" sx={{ borderRadius: 1, overflow: 'hidden' }}>
              <TableContainer sx={{ maxHeight: '62vh', overflowX: 'auto' }}>
                <Table
                  stickyHeader
                  size="small"
                  sx={{
                    minWidth: 1640,
                    tableLayout: 'fixed',
                    '& .MuiTableCell-root': { px: 2, py: 1.45 },
                    '& .MuiTableCell-root:first-of-type': {
                      position: 'sticky',
                      left: 0,
                      zIndex: 2,
                      bgcolor: 'background.paper',
                    },
                    '& .MuiTableHead-root .MuiTableCell-root': {
                      bgcolor: 'background.default',
                      color: 'text.secondary',
                      fontSize: 12,
                      fontWeight: 750,
                      whiteSpace: 'nowrap',
                      borderBottom: 1,
                      borderColor: 'divider',
                      textTransform: 'none !important',
                    },
                    '& .MuiTableHead-root .MuiTableCell-root:first-of-type': {
                      zIndex: 4,
                      bgcolor: 'background.default',
                    },
                    '& .MuiTableBody-root .MuiTableRow-root:last-child .MuiTableCell-root': {
                      borderBottom: 0,
                    },
                  }}
                >
                  <colgroup>
                    {HISTORY_COLUMN_WIDTHS.map((width, index) => (
                      <col key={`${index}-${width}`} style={{ width }} />
                    ))}
                  </colgroup>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('common.status')}</TableCell>
                      <TableCell>{t('emissionFactor.historyTable.changedAt')}</TableCell>
                      <TableCell>{t('emissionFactor.column.scopeCat')}</TableCell>
                      <TableCell>{t('emissionFactor.column.activity')}</TableCell>
                      <TableCell>{t('emissionFactor.field.fiscalYear')}</TableCell>
                      <TableCell>{t('emissionFactor.historyTable.action')}</TableCell>
                      <TableCell>{t('emissionFactor.historyTable.factorValue')}</TableCell>
                      <TableCell>{t('emissionFactor.column.unit')}</TableCell>
                      <TableCell>{t('emissionFactor.historyTable.sourceEvidence')}</TableCell>
                       <TableCell>{t('emissionFactor.historyTable.changedBy')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pageEntries.map((entry, index) => (
                      <TableRow hover key={entry.id || entry._id || index}>
                        <TableCell>
                          <Chip
                            size="small"
                            color={page * HISTORY_PAGE_SIZE + index === currentEntryIndex
                              ? 'success'
                              : 'default'}
                            variant={page * HISTORY_PAGE_SIZE + index === currentEntryIndex
                              ? 'filled'
                              : 'outlined'}
                            label={t(page * HISTORY_PAGE_SIZE + index === currentEntryIndex
                              ? 'emissionFactor.status.currentlyApplied'
                              : 'emissionFactor.status.superseded')}
                            sx={{ height: 24, fontSize: 11, fontWeight: 700 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {formatDate(entry.created_at)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            color="primary"
                            variant="outlined"
                            label={context?.scope || entry.after?.scope || entry.before?.scope || EMPTY_VALUE}
                            sx={{ height: 24, fontSize: 11, fontWeight: 700 }}
                          />
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            component="div"
                            sx={{ mt: 0.5 }}
                          >
                            {context?.categoryShort || entry.after?.category || entry.before?.category || EMPTY_VALUE}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ maxWidth: 250 }}>
                          <Typography variant="body2" fontWeight={700} noWrap title={context?.activity}>
                            {context?.activity || entry.after?.business_name || entry.after?.ef_name || entry.before?.business_name || entry.before?.ef_name || EMPTY_VALUE}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" noWrap component="div">
                            {[context?.categoryLabel, context?.externalId].filter(Boolean).join(' · ') || EMPTY_VALUE}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" fontWeight={700} sx={{ fontFamily: 'monospace' }}>
                            {context?.fiscalYear || entry.after?.effective_year || entry.after?.fiscal_year || entry.before?.effective_year || entry.before?.fiscal_year || EMPTY_VALUE}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            variant="outlined"
                            color={actionColor(entry)}
                            label={actionLabel(entry, t)}
                            sx={{ height: 24, fontSize: 11, fontWeight: 700 }}
                          />
                        </TableCell>
                        <TableCell><ValueSnapshot entry={entry} /></TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {entry.after?.factor_unit || entry.after?.ef_unit || entry.before?.factor_unit || entry.before?.ef_unit || EMPTY_VALUE}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="caption"
                            fontWeight={700}
                            noWrap
                            component="div"
                            title={snapshotOf(entry).source_label}
                          >
                            {snapshotOf(entry).source_label || EMPTY_VALUE}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            noWrap
                            component="div"
                            title={evidenceOf(snapshotOf(entry))}
                          >
                            {evidenceOf(snapshotOf(entry))}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={0.75} alignItems="center">
                            <PersonOutlineRoundedIcon sx={{ fontSize: 17, color: 'text.disabled' }} />
                            <Typography variant="caption" noWrap title={entry.created_by}>
                              {entry.created_by || EMPTY_VALUE}
                            </Typography>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {entries.length > HISTORY_PAGE_SIZE && (
                <TablePagination
                  component="div"
                  count={entries.length}
                  page={page}
                  rowsPerPage={HISTORY_PAGE_SIZE}
                  rowsPerPageOptions={[]}
                  onPageChange={(event, nextPage) => setPage(nextPage)}
                />
              )}
            </Paper>
          )}
        </Box>
      </DialogContent>
    </AppDialog>
  );
}
